import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { FieldLabel, PageToolbar } from '@/components/editorial';
import { FilterChips } from '@/components/common/FilterChips';
import { navigateToStevensTextFresh } from '@/lib/chat-navigation';
import { useCompanionName } from '@/hooks/useCompanionName';
import { conversationMatches, type ConversationScope } from '@/lib/conversation-filter';
import { ConversationRow } from './ConversationRow';
import { useConversations } from './useConversations';

/**
 * The full thread archive — the body of Settings › Companion › Conversations
 * (2026-09-21, spec `2026-09-21-parent-chrome-and-grants-design.md` Part 2).
 *
 * It is the former `/conversations` page, subtracted on Victor's three
 * points and re-homed. What changed from that page:
 *
 *   - No `PageHead`. The Settings page owns the H1, and the eyebrow-parity
 *     guard counts one `PageHead` per depth-0 page. The serif headline
 *     ("Your conversations with Maya.") and the lede are gone with it — a
 *     parent hub surface carries an eyebrow and nothing else (§2.1).
 *   - One `PageToolbar`: `+` · search · filter.
 *   - One line per row (`ConversationRow`).
 *   - Ten rows, then Show more.
 *   - "Updates in and around your family" retired. Those were task-activity
 *     rows, and Tasks owns tasks (§1.1, §3.5) — a slice of another page has
 *     no business inside Settings.
 *
 * The tri-state is carried over unchanged (AP-58): loading pulses, a failed
 * fetch says so with Retry, and only a RESOLVED fetch may claim "you have no
 * conversations".
 */

/** Rows on screen at first paint, and per Show more press. */
export const PAGE_SIZE = 10;

export function ConversationArchive() {
  const navigate = useNavigate();
  const { t } = useTranslation('companion');
  const companionName = useCompanionName();
  const conversationsQuery = useConversations();

  const [scope, setScope] = useState<ConversationScope>('all');
  const [search, setSearch] = useState('');
  const [visible, setVisible] = useState(PAGE_SIZE);

  const all = conversationsQuery.data ?? [];
  const matching = all.filter((c) => conversationMatches(c, scope, search));
  const shown = matching.slice(0, visible);

  // Narrowing resets the window. Without this, a search matching row 40
  // would need three Show more presses to be reachable — the list would be
  // filtering invisibly, which is exactly what PageToolbar's search contract
  // forbids.
  useEffect(() => { setVisible(PAGE_SIZE); }, [scope, search]);

  return (
    <div className="space-y-3">
      <PageToolbar
        label={t('archive.label')}
        add={{ items: [{ label: t('archive.newConversation'), onSelect: () => navigateToStevensTextFresh(navigate) }] }}
        search={{ value: search, onChange: setSearch, placeholder: t('history.search') }}
        filter={{
          active: scope !== 'all',
          label: t('archive.filterLabel'),
          panel: (
            <div className="space-y-2">
              <FieldLabel>{t('archive.show')}</FieldLabel>
              <FilterChips
                value={scope}
                onChange={(v) => setScope(v as ConversationScope)}
                options={[
                  { value: 'all', label: t('archive.scope.all') },
                  { value: 'voice', label: t('archive.scope.voice') },
                  { value: 'text', label: t('archive.scope.text') },
                ]}
              />
            </div>
          ),
        }}
      />

      <div className="rounded-lg border" data-testid="conversations-list">
        {conversationsQuery.isPending ? (
          <div className="p-4 space-y-3" aria-busy="true" aria-label={t('history.loading')}>
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}
          </div>
        ) : conversationsQuery.isError ? (
          <div className="flex items-center gap-3 px-4 py-3 text-body" role="alert">
            <span className="flex-1 min-w-0 text-destructive">
              {t('history.loadError')}
            </span>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-label px-2 flex-shrink-0"
              onClick={() => void conversationsQuery.refetch()}
            >
              {t('history.retry')}
            </Button>
          </div>
        ) : all.length === 0 ? (
          <p className="px-4 py-6 text-body text-stevens-ink-3">
            {t('history.empty', { companion: companionName })}
          </p>
        ) : matching.length === 0 ? (
          <p className="px-4 py-6 text-body text-stevens-ink-3">{t('history.noMatch')}</p>
        ) : (
          <>
            <ul className="divide-y">
              {shown.map((c) => (
                <li key={c.id}>
                  <ConversationRow conversation={c} onOpen={(id) => navigate(`/chat/${id}`)} />
                </li>
              ))}
            </ul>
            {matching.length > visible && (
              <div className="border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-label"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                >
                  {t('archive.showMore')}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ConversationArchive;
